import * as React from 'react'
import {
  User,
  Activity,
  Maximize,
  Minimize,
  Heart,
  ShoppingCart,
  Copy,
  CalendarDays,
  ChevronDown,
  CreditCard,
  History,
} from 'lucide-react'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from './breadcrumb'
import { Button } from './button'
import { Card, CardContent, CardHeader, CardTitle } from './card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from './dialog'
import { Input } from './input'
import { Label } from './label'
import { MedicationCard } from './medication-card'
import { MedicationAdjustmentForm, type MedicationAdjustmentData } from './medication-adjustment-form'
import { DateInput } from './date-input'
import { Separator } from './separator'
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
} from './sidebar'
import { Badge } from './badge'
import { Sonner, dismissToast } from './toast'
import { OrderAuditTrail } from './order-audit-trail'
import type { Patient } from '@joinomu/shared'
import { authService } from '../../../shared/src/auth/auth-service'
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select'
import { Textarea } from './textarea'
import { VisitsBookingDialog } from './visits-booking-dialog'
import { TrackingChart } from './tracking-chart'
import { PrescriptionHistory } from './prescription-history'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './popover'
import { supabase } from '@joinomu/shared'
import { cn } from '../lib/utils'

const patientNavData = {
  nav: [
    { name: "Information", icon: User },
    { name: "Preferred Medications", icon: Heart },
    { name: "Orders", icon: ShoppingCart },
    { name: "Billing", icon: CreditCard },
    { name: "Visits", icon: CalendarDays },
    { name: "History", icon: History },
    { name: "Tracking", icon: Activity },
  ],
}

export interface PatientMedicationPreference {
  id: string
  medication_name: string
  preferred_dosage: string
  frequency: string
  status: 'pending' | 'approved' | 'denied' | 'discontinued'
  requested_date: string
  notes?: string
  supply_days?: number
  other_dosage?: string
  medication_id?: string
}

export interface PatientMedicationOrder {
  id: string
  approval_id: string
  preference_id?: string | null
  medication_name: string
  quantity: number
  dosage: string
  total_amount: number
  payment_status: string
  payment_method?: string
  payment_date?: string
  fulfillment_status: string
  created_at: string
  shipped_date?: string
  tracking_number?: string
  estimated_delivery?: string
  admin_notes?: string
}

export interface PatientAppointment {
  id: string
  appointment_id: string
  provider_id: string
  provider_name: string
  appointment_date: string
  start_time: string
  treatment_type: string
  appointment_type: string
  status: string
  patient_notes?: string
}

export interface EnhancedPatientInformationDialogProps {
  patient: Patient | null
  preferredMedications?: PatientMedicationPreference[]
  medicationOrders?: PatientMedicationOrder[]
  appointments?: PatientAppointment[]
  availableMedications?: Array<{id: string, name: string, brand_name?: string, category: string, available_dosages?: string[]}>
  open: boolean
  onOpenChange: (open: boolean) => void
  isAdmin?: boolean
  onRescheduleAppointment?: (appointmentData: {
    appointmentId: string
    appointmentDate: string
    startTime: string
  }) => Promise<{ success: boolean; message?: string }>
  onGetAvailableSlots?: (
    providerId: string,
    startDate: string,
    endDate: string,
    treatmentType?: string
  ) => Promise<{ data: any[]; error?: any }>
  onMedicationChange?: (medicationId: string, medication: MedicationAdjustmentData) => Promise<void>
  onMedicationFax?: (medicationId: string, shouldFax: boolean, patientProfileId?: string, providerProfileId?: string) => Promise<void>
  providers?: any[]
  medicationPreferences?: any[]
  initialSection?: string
}

export function EnhancedPatientInformationDialog({
  patient,
  preferredMedications = [],
  medicationOrders = [],
  appointments = [],
  availableMedications = [],
  open,
  onOpenChange,
  isAdmin = false,
  onRescheduleAppointment,
  onGetAvailableSlots,
  onMedicationChange,
  onMedicationFax,
  providers = [],
  medicationPreferences = [],
  initialSection = "Information"
}: EnhancedPatientInformationDialogProps) {
  const [isFullscreen, setIsFullscreen] = React.useState(true)
  const [activeSection, setActiveSection] = React.useState(initialSection)
  const [selectedPreference, setSelectedPreference] = React.useState<PatientMedicationPreference | null>(null)
  const [selectedOrder, setSelectedOrder] = React.useState<PatientMedicationOrder | null>(null)
  const [associatedOrders, setAssociatedOrders] = React.useState<PatientMedicationOrder[]>([])
  
  // Order editing state
  const [editingOrder, setEditingOrder] = React.useState(false)
  const [orderPaymentStatus, setOrderPaymentStatus] = React.useState("")
  const [orderPaymentMethod, setOrderPaymentMethod] = React.useState("")
  const [orderPaymentDate, setOrderPaymentDate] = React.useState("")
  const [orderFulfillmentStatus, setOrderFulfillmentStatus] = React.useState("")
  const [orderTrackingNumber, setOrderTrackingNumber] = React.useState("")
  const [orderShippedDate, setOrderShippedDate] = React.useState("")
  const [orderEstimatedDelivery, setOrderEstimatedDelivery] = React.useState("")
  const [orderAdminNotes, setOrderAdminNotes] = React.useState("")
  
  // Reschedule dialog state
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = React.useState(false)
  const [selectedAppointment, setSelectedAppointment] = React.useState<PatientAppointment | null>(null)
  
  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false)

  // Tracking state
  const [healthMetricsData, setHealthMetricsData] = React.useState<any[]>([])
  const [medicationTrackingData, setMedicationTrackingData] = React.useState<any[]>([])
  const [currentMetricType, setCurrentMetricType] = React.useState<string>('weight')
  const [selectedMedicationsForTracking, setSelectedMedicationsForTracking] = React.useState<string[]>([])

  // Billing state
  const [medicationSubscriptions, setMedicationSubscriptions] = React.useState<any[]>([])
  const [loadingSubscriptions, setLoadingSubscriptions] = React.useState(false)
  const [paymentHistory, setPaymentHistory] = React.useState<any[]>([])
  const [isLoadingPaymentHistory, setIsLoadingPaymentHistory] = React.useState(false)
  const [paymentHistoryError, setPaymentHistoryError] = React.useState<string | null>(null)
  const [upcomingInvoice, setUpcomingInvoice] = React.useState<any>(null)
  const [isLoadingUpcomingInvoice, setIsLoadingUpcomingInvoice] = React.useState(false)

  // Track medication state locally with stable keys to prevent re-renders
  const [medicationStates, setMedicationStates] = React.useState<{[id: string]: MedicationAdjustmentData}>({})
  
  // Track whether fax should be sent when form is saved
  const [shouldFaxOnSave, setShouldFaxOnSave] = React.useState<{[medicationId: string]: boolean}>({})

  // Convert PatientMedicationPreference to MedicationAdjustmentData format
  const convertToMedicationAdjustmentData = React.useCallback((preference: PatientMedicationPreference): MedicationAdjustmentData => {
    return {
      id: preference.id,
      medication_id: preference.medication_id || '',
      medication_name: preference.medication_name,
      preferred_dosage: preference.preferred_dosage,
      frequency: preference.frequency,
      status: preference.status,
      requested_date: preference.requested_date,
      providerNotes: preference.notes,
      supply_days: preference.supply_days,
    }
  }, [])


  // Load health metrics data for the patient
  const loadHealthMetrics = React.useCallback(async (metricType: string = 'weight') => {
    if (!patient?.profile_id) return

    console.log('🔍 ENHANCED DIALOG: Loading health metrics for patient:', patient.profile_id, 'metric type:', metricType)

    try {
      // First get the patient's internal ID from profile_id
      const { data: patientData, error: patientError } = await supabase
        .from('patients')
        .select('*')
        .eq('profile_id', patient.profile_id)
        .single()

      if (patientError || !patientData) {
        console.error('❌ ENHANCED DIALOG: Failed to get patient ID:', patientError)
        setHealthMetricsData([])
        return
      }

      const patientId = patientData.id
      console.log('🔍 ENHANCED DIALOG: Found patient database ID:', patientId)

      // Now fetch health metrics using direct API call
      const startDate = new Date()
      startDate.setFullYear(startDate.getFullYear() - 1) // 1 year ago
      const endDate = new Date()

      // Map frontend metric types to database metric types
      const dbMetricType = metricType === 'heart-rate' ? 'heart_rate' : metricType

      const apiUrl = `/api/patients/health-metrics?patient_id=${patientId}&metric_type=${dbMetricType}&limit=1000&start_date=${startDate.toISOString()}&end_date=${endDate.toISOString()}`
      console.log('🔍 ENHANCED DIALOG: Fetching health metrics with URL:', apiUrl)

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error('❌ ENHANCED DIALOG: Health metrics fetch error:', errorText)
        setHealthMetricsData([])
        return
      }

      const metricsResult = await response.json()
      const rawMetrics = metricsResult.data || []
      console.log('✅ ENHANCED DIALOG: Health metrics fetch success. Raw data count:', rawMetrics?.length)

      // Transform to expected format
      const transformedMetrics = (rawMetrics || []).map((metric: any) => ({
        date: metric.recorded_at.split('T')[0], // Convert to YYYY-MM-DD format
        value: metric.value,
        unit: metric.unit
      }))

      console.log('🔍 ENHANCED DIALOG: Transformed metrics count:', transformedMetrics.length)
      setHealthMetricsData(transformedMetrics)

    } catch (error) {
      console.error('❌ ENHANCED DIALOG: Error loading health metrics:', error)
      setHealthMetricsData([])
    }
  }, [patient?.profile_id])

  // Load medication tracking data for the patient
  const loadMedicationTracking = React.useCallback(async () => {
    if (!patient?.profile_id) return

    console.log('🔍 ENHANCED DIALOG: Loading medication tracking for patient:', patient.profile_id)

    try {
      // First get the patient's internal ID from profile_id
      const { data: patientData, error: patientError } = await supabase
        .from('patients')
        .select('*')
        .eq('profile_id', patient.profile_id)
        .single()

      if (patientError || !patientData) {
        console.error('❌ ENHANCED DIALOG: Failed to get patient ID for medication tracking:', patientError)
        setMedicationTrackingData([])
        return
      }

      const patientId = patientData.id
      console.log('🔍 ENHANCED DIALOG: Found patient database ID for medication tracking:', patientId)

      // Now fetch medication tracking entries with proper enrichment including medication names
      const apiUrl = `/api/patients/medication-tracking?patient_id=${patientId}&limit=1000`
      console.log('🔍 ENHANCED DIALOG: Fetching medication tracking with URL:', apiUrl)

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error('❌ ENHANCED DIALOG: Medication tracking fetch error:', errorText)
        setMedicationTrackingData([])
        return
      }

      const trackingResult = await response.json()
      const rawEntries = trackingResult.data || []
      console.log('✅ ENHANCED DIALOG: Medication tracking fetch success. Entry count:', rawEntries?.length)

      if (rawEntries && rawEntries.length > 0) {
        console.log('✅ ENHANCED DIALOG: Using enriched medication tracking entries from API')
        setMedicationTrackingData(rawEntries)
      } else {
        console.log('🔍 ENHANCED DIALOG: No medication tracking data found')
        setMedicationTrackingData([])
      }
    } catch (error) {
      console.error('❌ ENHANCED DIALOG: Error loading medication tracking:', error)
      setMedicationTrackingData([])
    }
  }, [patient?.profile_id])

  // Load medication subscriptions for the patient
  const loadMedicationSubscriptions = React.useCallback(async () => {
    if (!patient?.profile_id) return

    console.log('🔍 ENHANCED DIALOG: Loading medication subscriptions for patient:', patient.profile_id)
    setLoadingSubscriptions(true)

    try {
      // First get the patient's internal ID from profile_id
      const { data: patientData, error: patientError } = await supabase
        .from('patients')
        .select('*')
        .eq('profile_id', patient.profile_id)
        .single()

      if (patientError || !patientData) {
        console.error('❌ ENHANCED DIALOG: Failed to get patient ID for subscriptions:', patientError)
        setMedicationSubscriptions([])
        return
      }

      const patientId = patientData.id
      console.log('🔍 ENHANCED DIALOG: Found patient database ID for subscriptions:', patientId)

      // Get medication preferences with active subscriptions
      const { data: subscriptionsData, error: subscriptionsError } = await supabase
        .from('patient_medication_preferences')
        .select(`
          *,
          medications (
            name,
            medication_type,
            monthly_price_id,
            quarterly_price_id
          )
        `)
        .eq('patient_id', patientId)
        .not('stripe_subscription_id', 'is', null)

      if (subscriptionsError) {
        console.error('❌ ENHANCED DIALOG: Failed to load medication subscriptions:', subscriptionsError)
        setMedicationSubscriptions([])
        return
      }

      console.log('✅ ENHANCED DIALOG: Medication subscriptions loaded:', subscriptionsData?.length || 0)
      setMedicationSubscriptions(subscriptionsData || [])

    } catch (error) {
      console.error('❌ ENHANCED DIALOG: Error loading medication subscriptions:', error)
      setMedicationSubscriptions([])
    } finally {
      setLoadingSubscriptions(false)
    }
  }, [patient?.profile_id])

  // Load payment history for the patient (similar to patient app)
  const loadPaymentHistory = React.useCallback(async () => {
    if (!patient?.profile_id) return

    console.log('🔍 ENHANCED DIALOG: Loading payment history for patient:', patient.profile_id)
    setIsLoadingPaymentHistory(true)
    setPaymentHistoryError(null)

    try {
      // First get the patient's internal ID from profile_id
      const { data: patientData, error: patientError } = await supabase
        .from('patients')
        .select('*')
        .eq('profile_id', patient.profile_id)
        .single()

      if (patientError || !patientData) {
        console.error('❌ ENHANCED DIALOG: Failed to get patient ID for payment history:', patientError)
        setPaymentHistory([])
        return
      }

      const patientId = patientData.id
      console.log('🔍 ENHANCED DIALOG: Found patient database ID for payment history:', patientId)

      // Use the StripeService to get payment history
      const module = await import('@joinomu/shared')
      const result = await module.StripeService.getPaymentHistory(patientId)

      if (result.error) {
        setPaymentHistoryError('Failed to load payment history')
        console.error('❌ ENHANCED DIALOG: Payment history load error:', result.error)
      } else {
        console.log('✅ ENHANCED DIALOG: Payment history loaded:', result.data?.length, 'payments')
        setPaymentHistory(result.data || [])
      }
    } catch (error) {
      setPaymentHistoryError('Failed to load payment history')
      console.error('❌ ENHANCED DIALOG: Error loading payment history:', error)
      setPaymentHistory([])
    } finally {
      setIsLoadingPaymentHistory(false)
    }
  }, [patient?.profile_id])

  // Load upcoming invoice for credit notification
  const loadUpcomingInvoice = React.useCallback(async () => {
    if (!patient?.profile_id) return

    console.log('🔍 ENHANCED DIALOG: Loading upcoming invoice for patient:', patient.profile_id)
    setIsLoadingUpcomingInvoice(true)

    try {
      // First get the patient's internal ID from profile_id
      const { data: patientData, error: patientError } = await supabase
        .from('patients')
        .select('*')
        .eq('profile_id', patient.profile_id)
        .single()

      if (patientError || !patientData) {
        console.error('❌ ENHANCED DIALOG: Failed to get patient ID for upcoming invoice:', patientError)
        setUpcomingInvoice(null)
        return
      }

      const patientId = patientData.id
      console.log('🔍 ENHANCED DIALOG: Found patient database ID for upcoming invoice:', patientId)

      // Use the StripeService to get upcoming invoice
      const module = await import('@joinomu/shared')
      const result = await module.StripeService.getUpcomingInvoice(patientId)

      if (result.error) {
        console.error('❌ ENHANCED DIALOG: Upcoming invoice load error:', result.error)
        setUpcomingInvoice(null)
      } else {
        console.log('✅ ENHANCED DIALOG: Upcoming invoice loaded with customer balance:', result.data?.customerBalance)
        setUpcomingInvoice(result.data || null)
      }
    } catch (error) {
      console.error('❌ ENHANCED DIALOG: Error loading upcoming invoice:', error)
      setUpcomingInvoice(null)
    } finally {
      setIsLoadingUpcomingInvoice(false)
    }
  }, [patient?.profile_id])

  // Handle metric type change for tracking chart
  const handleMetricChange = async (metricType: string) => {
    setCurrentMetricType(metricType)
    await loadHealthMetrics(metricType)
  }

  // Handle refresh for tracking chart
  const handleRefreshMetrics = () => {
    loadHealthMetrics(currentMetricType)
    loadMedicationTracking()
  }

  // Handle medication selection change for tracking chart
  const handleMedicationSelectionChange = (selectedIds: string[]) => {
    console.log('🔍 ENHANCED DIALOG: Medication selection changed:', selectedIds)
    setSelectedMedicationsForTracking(selectedIds)
  }

  // Load tracking data when Tracking section is selected
  React.useEffect(() => {
    if (activeSection === 'Tracking' && patient?.profile_id) {
      console.log('🔍 ENHANCED DIALOG: Tracking section selected, loading data...')
      loadHealthMetrics(currentMetricType)
      loadMedicationTracking()
    }
  }, [activeSection, patient?.profile_id, currentMetricType, loadHealthMetrics, loadMedicationTracking])

  // Load billing data when Billing section is selected
  React.useEffect(() => {
    if (activeSection === 'Billing' && patient?.profile_id) {
      console.log('🔍 ENHANCED DIALOG: Billing section selected, loading billing data...')
      loadMedicationSubscriptions()
      loadPaymentHistory()
      loadUpcomingInvoice()
    }
  }, [activeSection, patient?.profile_id, loadMedicationSubscriptions, loadPaymentHistory, loadUpcomingInvoice])

  // Load associated orders when a preference is selected
  React.useEffect(() => {
    const fetchAssociatedOrders = async () => {
      if (selectedPreference) {
        try {
          console.log('🔍 UI: Fetching orders for preference:', selectedPreference.id, 'REF:', selectedPreference.id.slice(0, 8).toUpperCase())
          const { data } = await authService.getOrdersByPreferenceId(selectedPreference.id)
          console.log('📦 UI: Received orders:', data?.length || 0, 'orders')
          setAssociatedOrders(data || [])
        } catch (error) {
          console.error('❌ UI: Error fetching associated orders:', error)
          setAssociatedOrders([])
        }
      } else {
        setAssociatedOrders([])
      }
    }

    fetchAssociatedOrders()
  }, [selectedPreference])

  // Load selected order data when changed
  React.useEffect(() => {
    if (selectedOrder) {
      setOrderPaymentStatus(selectedOrder.payment_status || "")
      setOrderPaymentMethod(selectedOrder.payment_method || "")
      setOrderPaymentDate(selectedOrder.payment_date || "")
      setOrderFulfillmentStatus(selectedOrder.fulfillment_status || "")
      setOrderTrackingNumber(selectedOrder.tracking_number || "")
      setOrderShippedDate(selectedOrder.shipped_date || "")
      setOrderEstimatedDelivery(selectedOrder.estimated_delivery || "")
      setOrderAdminNotes(selectedOrder.admin_notes || "")
      setEditingOrder(false)
    }
  }, [selectedOrder])

  if (!patient) {
    return null
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return "Not provided"
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const formatName = (first_name: string, last_name: string) => {
    return `${first_name} ${last_name}`.trim() || "Unknown Patient"
  }

  const getIntakeStatus = (has_completed_intake?: boolean) => {
    if (has_completed_intake === true) {
      return "Complete"
    } else if (has_completed_intake === false) {
      return "Pending"
    }
    return "Unknown"
  }

  const getTreatmentPlan = () => {
    if (patient.treatment_types && patient.treatment_types.length > 0) {
      return patient.treatment_types.join(', ').replace(/_/g, ' ')
    }
    return "No active treatment plan"
  }

  const getPrimaryProvider = () => {
    if (patient.assigned_providers && patient.assigned_providers.length > 0) {
      return patient.assigned_providers[0]
    }
    return "No provider assigned"
  }

  const handleSaveOrder = async () => {
    if (selectedOrder && isAdmin) {
      let savingToastId: string | number | undefined
      
      try {
        // Show loading toast
        savingToastId = Sonner.saving('Order ' + selectedOrder.id.slice(0, 8))
        
        // Call the update function
        const result = await authService.updateMedicationOrderAdmin(selectedOrder.id, {
          payment_status: orderPaymentStatus,
          payment_method: orderPaymentMethod,
          payment_date: orderPaymentDate,
          fulfillment_status: orderFulfillmentStatus,
          tracking_number: orderTrackingNumber,
          shipped_date: orderShippedDate,
          estimated_delivery: orderEstimatedDelivery,
          admin_notes: orderAdminNotes
        })
        
        // Dismiss the saving toast
        if (savingToastId) {
          dismissToast(savingToastId)
        }
        
        if (result.success) {
          // Update the selectedOrder object with the new data from the database
          setSelectedOrder({
            ...selectedOrder,
            payment_status: orderPaymentStatus,
            payment_method: orderPaymentMethod,
            payment_date: orderPaymentDate,
            fulfillment_status: orderFulfillmentStatus,
            tracking_number: orderTrackingNumber,
            shipped_date: orderShippedDate,
            estimated_delivery: orderEstimatedDelivery,
            admin_notes: orderAdminNotes
          })
          
          // Show success toast
          Sonner.saved('Order ' + selectedOrder.id.slice(0, 8))
          setEditingOrder(false)
        } else {
          // Show error toast
          Sonner.error('Order ' + selectedOrder.id.slice(0, 8), result.error?.message || 'Unknown error')
        }
        
      } catch (error) {
        // Dismiss the saving toast
        if (savingToastId) {
          dismissToast(savingToastId)
        }
        
        // Show error toast
        Sonner.error('Order ' + selectedOrder.id.slice(0, 8), error instanceof Error ? error.message : 'Unknown error')
      }
    }
  }

  const getCurrentBreadcrumb = () => {
    if (selectedPreference) {
      return (
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem className="hidden md:block">
              <BreadcrumbLink
                href="#"
                onClick={() => {
                  setActiveSection("Patient Information")
                  setSelectedPreference(null)
                  setSelectedOrder(null)
                }}
              >
                Patient Information
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="hidden md:block" />
            <BreadcrumbItem>
              <BreadcrumbLink 
                href="#" 
                onClick={() => setSelectedPreference(null)}
              >
                Preferred Medications
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{selectedPreference.medication_name}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      )
    }

    if (selectedOrder) {
      return (
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem className="hidden md:block">
              <BreadcrumbLink
                href="#"
                onClick={() => {
                  setActiveSection("Patient Information")
                  setSelectedPreference(null)
                  setSelectedOrder(null)
                }}
              >
                Patient Information
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="hidden md:block" />
            <BreadcrumbItem>
              <BreadcrumbLink
                href="#"
                onClick={() => setSelectedOrder(null)}
              >
                Orders
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{selectedOrder.medication_name}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      )
    }

    return (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem className="hidden md:block">
            <BreadcrumbLink href="#">Patient Information</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator className="hidden md:block" />
          <BreadcrumbItem>
            <BreadcrumbPage>{activeSection}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    )
  }

  const renderContent = () => {
    if (selectedPreference) {
      // Medication adjustment form view - same as clinical notes
      return (
        <div className="w-full h-full overflow-y-auto">
          <div className="space-y-6">
            {/* Reference Information */}
            <Card className="border-0 shadow-none">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <Label className="text-sm font-medium">Reference Number</Label>
                    <p className="text-sm text-muted-foreground">REF-{selectedPreference.id.slice(0, 8).toUpperCase()}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Requested Date</Label>
                    <p className="text-sm">{new Date(selectedPreference.requested_date).toLocaleDateString()}</p>
                  </div>
                </div>

                {/* Medication Adjustments - Exact copy from clinical note form */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Medication Adjustments</h3>
                  <div className="space-y-4">
                    {(() => {
                      const medication = convertToMedicationAdjustmentData(selectedPreference)
                      const currentMedicationState = medicationStates[medication.id] || medication
                      
                      const handleMedicationChange = (updatedMedication: MedicationAdjustmentData) => {
                        console.log('🔍 PATIENT INFO DIALOG: Medication form changed:', updatedMedication.id)
                        
                        // Update medication states with the new data
                        setMedicationStates(prev => ({
                          ...prev,
                          [updatedMedication.id]: updatedMedication
                        }))
                        
                        // Notify parent component
                        onMedicationChange?.(updatedMedication.id, updatedMedication)
                      }

                      const handleFaxChange = async (shouldFax: boolean) => {
                        console.log('🔍 Fax checkbox clicked for medication:', medication.id, '- shouldFax:', shouldFax)
                        
                        // Track fax intent for when form is saved
                        setShouldFaxOnSave(prev => ({
                          ...prev,
                          [medication.id]: shouldFax
                        }))
                        
                        // Update medication state to show fax intent
                        const updatedMedication = {
                          ...currentMedicationState,
                          faxed: shouldFax ? 'pending' : null
                        }
                        
                        setMedicationStates(prev => ({
                          ...prev,
                          [medication.id]: updatedMedication
                        }))
                        
                        onMedicationChange?.(medication.id, updatedMedication)
                        
                        // Call the fax function if provided
                        if (onMedicationFax && patient?.profile_id) {
                          try {
                            await onMedicationFax(medication.id, shouldFax, patient.profile_id, undefined)
                          } catch (error) {
                            console.error('Error handling fax:', error)
                          }
                        }
                      }

                      return (
                        <MedicationAdjustmentForm
                          key={medication.id}
                          medication={currentMedicationState}
                          availableMedications={availableMedications}
                          showFaxCheckbox={true}
                          showMedicationSelector={true}
                          onChange={handleMedicationChange}
                          onFax={handleFaxChange}
                          showSaveButton={false}
                          showRequestedDate={false}
                        />
                      )
                    })()}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Associated Orders */}
            {associatedOrders.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Associated Orders</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {associatedOrders.map((order) => (
                      <div key={order.id} className="p-3 border rounded-md bg-muted/30">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium">REF-{order.id.slice(0, 8).toUpperCase()}</p>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() => {
                                  navigator.clipboard.writeText(`REF-${order.id.slice(0, 8).toUpperCase()}`)
                                  Sonner.copied(`REF-${order.id.slice(0, 8).toUpperCase()}`)
                                }}
                              >
                                <Copy className="h-4 w-4" />
                                <span className="sr-only">Copy reference number</span>
                              </Button>
                            </div>
                            <p className="text-xs text-muted-foreground">{new Date(order.created_at).toLocaleDateString()}</p>
                          </div>
                          <p className="text-sm font-semibold">${order.total_amount?.toFixed(2) || '0.00'}</p>
                        </div>
                        <div className="flex gap-2">
                          <Badge variant={
                            order.fulfillment_status === 'delivered' ? 'default' :
                            order.fulfillment_status === 'shipped' ? 'secondary' :
                            order.fulfillment_status === 'pharmacy_fulfilled' ? 'secondary' :
                            order.fulfillment_status === 'processing' ? 'outline' : 'outline'
                          } className="text-xs">
                            {order.fulfillment_status === 'pharmacy_fulfilled' ? 'Pharmacy Fulfilled' : order.fulfillment_status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )
    }

    if (selectedOrder) {
      // Medication order detail view with admin editing
      return (
        <div className="w-full h-full flex flex-col">
          {/* Fixed Medication Name */}
          <div className="flex-shrink-0 pb-4 border-b border-border">
            <Label className="text-sm font-medium">Medication Name</Label>
            <p className="text-lg font-semibold">{selectedOrder.medication_name}</p>
          </div>
          
          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto pt-4" style={{minHeight: 0}}>
            <div className="space-y-6 pb-8">
              {/* Basic Order Info */}
              <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div>
                  <Label className="text-sm font-medium">Dosage</Label>
                  <p>{selectedOrder.dosage}</p>
                </div>
                <Separator orientation="vertical" className="h-8" />
                <div>
                  <Label className="text-sm font-medium">Quantity</Label>
                  <p>{selectedOrder.quantity}</p>
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Total Amount</Label>
                <p className="text-lg font-semibold">${selectedOrder.total_amount?.toFixed(2) || '0.00'}</p>
              </div>
              <div>
                <Label className="text-sm font-medium">Order Date</Label>
                <p>{selectedOrder.payment_date ? new Date(selectedOrder.payment_date).toLocaleDateString() : 'Pending'}</p>
              </div>
              {selectedOrder.preference_id && (
                <div>
                  <Label className="text-sm font-medium">Preference</Label>
                  <p className="text-sm text-muted-foreground">REF-{selectedOrder.preference_id.slice(0, 8).toUpperCase()}</p>
                </div>
              )}
              <div>
                <Label className="text-sm font-medium">Order</Label>
                <p className="text-sm text-muted-foreground">REF-{selectedOrder.id.slice(0, 8).toUpperCase()}</p>
              </div>
            </div>

            {isAdmin && (
              <>
                <Separator />
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Admin Controls</h3>
                  <Button
                    variant={editingOrder ? "outline" : "default"}
                    onClick={() => setEditingOrder(!editingOrder)}
                    size="sm"
                  >
                    {editingOrder ? "Cancel" : "Edit"}
                  </Button>
                </div>

                {editingOrder ? (
                  /* Edit Mode */
                  <div className="space-y-4">
                    {/* Payment Status & Fulfillment Status - Two Column */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Payment Status</Label>
                        <Select value={orderPaymentStatus} onValueChange={setOrderPaymentStatus}>
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="paid">Paid</SelectItem>
                            <SelectItem value="failed">Failed</SelectItem>
                            <SelectItem value="refunded">Refunded</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Fulfillment Status</Label>
                        <Select value={orderFulfillmentStatus} onValueChange={setOrderFulfillmentStatus}>
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="processing">Processing</SelectItem>
                            <SelectItem value="pharmacy_fulfilled">Pharmacy Fulfilled</SelectItem>
                            <SelectItem value="shipped">Shipped</SelectItem>
                            <SelectItem value="delivered">Delivered</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Payment Method & Date - Two Column */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Payment Method</Label>
                        <Input
                          value={orderPaymentMethod}
                          onChange={(e) => setOrderPaymentMethod(e.target.value)}
                          placeholder="e.g., Credit Card, Insurance"
                          className="w-full"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Payment Date</Label>
                        <DateInput
                          label=""
                          value={orderPaymentDate}
                          placeholder="Select payment date"
                          onChange={setOrderPaymentDate}
                          id="order-payment-date"
                          className="w-full"
                        />
                      </div>
                    </div>

                    {/* Tracking Number - Full Width */}
                    <div className="space-y-2">
                      <Label>Tracking Number</Label>
                      <Input
                        value={orderTrackingNumber}
                        onChange={(e) => setOrderTrackingNumber(e.target.value)}
                        placeholder="Enter tracking number"
                        className="w-full"
                      />
                    </div>

                    {/* Shipped Date & Estimated Delivery - Two Column */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Shipped Date</Label>
                        <DateInput
                          label=""
                          value={orderShippedDate}
                          placeholder="Select shipped date"
                          onChange={setOrderShippedDate}
                          id="order-shipped-date"
                          className="w-full"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Estimated Delivery</Label>
                        <DateInput
                          label=""
                          value={orderEstimatedDelivery}
                          placeholder="Select estimated delivery"
                          onChange={setOrderEstimatedDelivery}
                          id="order-estimated-delivery"
                          className="w-full"
                        />
                      </div>
                    </div>

                    {/* Admin Notes - Full Width */}
                    <div className="space-y-2">
                      <Label>Admin Notes</Label>
                      <Textarea
                        value={orderAdminNotes}
                        onChange={(e) => setOrderAdminNotes(e.target.value)}
                        placeholder="Internal notes for this order..."
                        rows={3}
                      />
                    </div>

                    <div className="flex justify-end pt-4 pb-6">
                      <Button onClick={handleSaveOrder}>
                        Save Changes
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* View Mode */
                  <div className="space-y-4">
                    <div className="flex items-start gap-4">
                      <div>
                        <Label className="text-sm font-medium">Payment Status</Label>
                        <div className="mt-1">
                          <Badge variant={
                            selectedOrder.payment_status === 'paid' ? 'default' :
                            selectedOrder.payment_status === 'pending' ? 'secondary' :
                            selectedOrder.payment_status === 'failed' ? 'destructive' : 'outline'
                          }>
                            {selectedOrder.payment_status}
                          </Badge>
                        </div>
                      </div>
                      <Separator orientation="vertical" className="h-12" />
                      <div>
                        <Label className="text-sm font-medium">Fulfillment Status</Label>
                        <div className="mt-1">
                          <Badge variant={
                            selectedOrder.fulfillment_status === 'delivered' ? 'default' :
                            selectedOrder.fulfillment_status === 'shipped' ? 'secondary' :
                            selectedOrder.fulfillment_status === 'pharmacy_fulfilled' ? 'secondary' :
                            selectedOrder.fulfillment_status === 'processing' ? 'outline' : 'outline'
                          }>
                            {selectedOrder.fulfillment_status === 'pharmacy_fulfilled' ? 'Pharmacy Fulfilled' : selectedOrder.fulfillment_status}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    
                    {selectedOrder.payment_method && (
                      <div>
                        <Label className="text-sm font-medium">Payment Method</Label>
                        <p>{selectedOrder.payment_method}</p>
                      </div>
                    )}
                    
                    {selectedOrder.payment_date && (
                      <div>
                        <Label className="text-sm font-medium">Payment Date</Label>
                        <p>{new Date(selectedOrder.payment_date).toLocaleDateString()}</p>
                      </div>
                    )}
                    
                    {selectedOrder.tracking_number && (
                      <div>
                        <Label className="text-sm font-medium">Tracking Number</Label>
                        <p className="font-mono text-sm">{selectedOrder.tracking_number}</p>
                      </div>
                    )}
                    
                    {selectedOrder.shipped_date && (
                      <div>
                        <Label className="text-sm font-medium">Shipped Date</Label>
                        <p>{new Date(selectedOrder.shipped_date).toLocaleDateString()}</p>
                      </div>
                    )}
                    
                    {selectedOrder.estimated_delivery && (
                      <div>
                        <Label className="text-sm font-medium">Estimated Delivery</Label>
                        <p>{new Date(selectedOrder.estimated_delivery).toLocaleDateString()}</p>
                      </div>
                    )}
                    
                    {selectedOrder.admin_notes && (
                      <div>
                        <Label className="text-sm font-medium">Admin Notes</Label>
                        <p className="text-sm text-muted-foreground">{selectedOrder.admin_notes}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Order Modification History */}
                <div className="border-t pt-6 mt-6">
                  <OrderAuditTrail 
                    orderId={selectedOrder.id}
                    className="w-full"
                  />
                </div>
              </>
            )}
            </div>
          </div>
        </div>
      )
    }

    if (activeSection === "Preferred Medications") {
      // Preferred medications list view
      return (
        <div className="space-y-4">
          {preferredMedications.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No preferred medications found for this patient.
            </div>
          ) : (
            <div className="space-y-4">
              {preferredMedications.map((preference) => (
                <MedicationCard
                  key={preference.id}
                  medicationName={preference.medication_name}
                  dosage={preference.preferred_dosage}
                  supply={preference.frequency}
                  status={preference.status}
                  orderNumber={`REF-${preference.id.slice(0, 8)}`}
                  onClick={() => setSelectedPreference(preference)}
                  className="w-full max-w-none cursor-pointer"
                />
              ))}
            </div>
          )}

          <div className="text-sm text-muted-foreground">
            Click on any preference card to view detailed information from the onboarding process.
          </div>
        </div>
      )
    }

    if (activeSection === "Orders") {
      // Medication orders list view
      return (
        <div className="space-y-4">
          {medicationOrders.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No medication orders found for this patient.
            </div>
          ) : (
            <div className="space-y-4">
              {medicationOrders.map((order) => (
                <MedicationCard
                  key={order.id}
                  medicationName={order.medication_name}
                  dosage={order.dosage}
                  supply={`Qty: ${order.quantity}`}
                  status={order.fulfillment_status as "active" | "pending" | "approved" | "denied" | "discontinued" | "shipped" | "delivered" | "scheduled"}
                  orderDate={order.payment_date ? new Date(order.payment_date).toLocaleDateString() : 'Pending'}
                  orderNumber={`REF-${order.id.slice(0, 8)}`}
                  onClick={() => setSelectedOrder(order)}
                  className="w-full max-w-none cursor-pointer"
                />
              ))}
            </div>
          )}

          <div className="text-sm text-muted-foreground">
            Click on any order card to view detailed payment and fulfillment information.
          </div>
        </div>
      )
    }

    if (activeSection === "Billing") {
      // Patient billing section - view plan and payment history
      // Determine plan details based on subscription_plan field OR active medication subscriptions OR payment history
      const getPlanDetails = () => {
        const subscriptionPlan = patient?.subscription_plan
        const hasActiveMedicationSubscriptions = medicationSubscriptions.length > 0
        const hasActiveSubscriptionStatus = patient?.subscription_status === 'active' || patient?.subscription_status === 'trialing'
        const hasPaymentHistory = paymentHistory.length > 0

        // If patient has payment history, they have an active subscription
        // If patient has active medication subscriptions in Stripe, they have Medication + Tracking
        if (hasPaymentHistory || hasActiveMedicationSubscriptions || (hasActiveSubscriptionStatus && subscriptionPlan && subscriptionPlan !== 'tracking')) {
          return {
            name: 'Medication + Tracking',
            description: 'Active subscription with medication and tracking',
            hasActivePlan: true
          }
        } else if (subscriptionPlan === 'tracking') {
          return {
            name: 'Tracking Only',
            description: '$0/month - Health tracking features only',
            hasActivePlan: true
          }
        } else if (hasActiveSubscriptionStatus) {
          // Has active subscription but no specific plan - assume Medication + Tracking
          return {
            name: 'Medication + Tracking',
            description: 'Active subscription with medication and tracking',
            hasActivePlan: true
          }
        } else {
          return {
            name: 'No Plan Selected',
            description: 'Patient has not selected a plan yet',
            hasActivePlan: false
          }
        }
      }

      const planDetails = getPlanDetails()

      return (
        <div className="space-y-4">
          {/* Plan Section */}
          <div className="bg-muted/50 rounded-xl p-4">
            <h3 className="font-semibold mb-4">Plan</h3>
            <div className="p-3 border rounded-md bg-background">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium">{planDetails.name}</p>
                  <p className="text-sm text-muted-foreground">{planDetails.description}</p>
                </div>
                <Badge variant={planDetails.hasActivePlan ? 'default' : 'outline'}>
                  {planDetails.hasActivePlan ?
                    (patient.subscription_status === 'active' ? 'Active' :
                     patient.subscription_status === 'trialing' ? 'Trialing' :
                     patient.subscription_status === 'past_due' ? 'Past Due' :
                     patient.subscription_status || 'Active')
                    : 'No Plan'}
                </Badge>
              </div>
              {patient.current_period_end && planDetails.hasActivePlan && (
                <div className="mt-3 pt-3 border-t text-sm">
                  <span className="text-muted-foreground">Next Billing: </span>
                  <span>{new Date(patient.current_period_end).toLocaleDateString()}</span>
                </div>
              )}
            </div>
          </div>

          {/* All Payments Section */}
          <div className="bg-muted/50 rounded-xl p-4">
            <h3 className="font-semibold mb-4">All Payments</h3>
            
            {/* Credit notification for admin view */}
            {upcomingInvoice && upcomingInvoice.customerBalance > 0 && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  Patient has a credit of ${upcomingInvoice.customerBalance.toFixed(2)} which will be applied to their next payment.
                </p>
              </div>
            )}
            
            <div className="space-y-3">
              {paymentHistoryError && (
                <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
                  {paymentHistoryError}
                </div>
              )}

              {isLoadingPaymentHistory ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p>Loading payment history...</p>
                </div>
              ) : paymentHistory.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No payments found.</p>
                  <p className="text-xs mt-2">Payments will appear here after the patient's first transaction.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {paymentHistory.slice(0, 10).map((payment) => (
                    <div key={payment.id} className="flex items-center justify-between p-3 border rounded-lg bg-background">
                      <div>
                        <p className="font-medium">{payment.description}</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(payment.created).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            payment.status === 'succeeded' || payment.status === 'paid'
                              ? 'bg-green-100 text-green-800'
                              : payment.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {payment.status === 'succeeded' ? 'Paid' : payment.status}
                          </span>
                          {(payment.receipt_url || payment.hosted_invoice_url) && (
                            <a
                              href={payment.receipt_url || payment.hosted_invoice_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:text-blue-800"
                            >
                              View Receipt
                            </a>
                          )}
                        </div>
                      </div>
                      <p className="font-semibold">${payment.amount.toFixed(2)}</p>
                    </div>
                  ))}
                  {paymentHistory.length > 10 && (
                    <div className="text-center py-2">
                      <span className="text-sm text-muted-foreground">
                        Showing recent 10 of {paymentHistory.length} payments
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="text-sm text-muted-foreground">
            View patient's plan details and complete payment history from Stripe.
          </div>
        </div>
      )
    }

    if (activeSection === "Visits") {
      // Patient visits list view with reschedule capability using MedicationCard
      return (
        <div className="space-y-4">
          {appointments.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No visits found for this patient.
            </div>
          ) : (
            <div className="space-y-4">
              {appointments.map((appointment) => {
                // Check if appointment is in the past
                const [year, month, day] = appointment.appointment_date.split('-').map(Number)
                const appointmentDate = new Date(year, month - 1, day) // month is 0-indexed
                const today = new Date()
                today.setHours(0, 0, 0, 0) // Set to start of day for accurate comparison
                appointmentDate.setHours(0, 0, 0, 0)
                
                const isInPast = appointmentDate < today
                
                // Determine the actual status - if in the past and still "scheduled", show as "completed"
                const actualStatus = (isInPast && appointment.status === 'scheduled') ? 'active' : appointment.status
                
                return (
                  <MedicationCard
                    key={appointment.id}
                    medicationName={appointment.provider_name}
                    dosage={appointment.treatment_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    supply={`${appointmentDate.toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })} at ${appointment.start_time}`}
                    status={actualStatus as "active" | "pending" | "approved" | "denied" | "discontinued" | "shipped" | "delivered" | "scheduled"}
                    onTitleClick={isAdmin && !isInPast && appointment.status === 'scheduled' ? () => {
                      setSelectedAppointment(appointment)
                      setRescheduleDialogOpen(true)
                    } : undefined}
                    className="w-full max-w-none"
                  />
                )
              })}
            </div>
          )}

          <div className="text-sm text-muted-foreground">
            {isAdmin
              ? "Click on the provider name to reschedule any scheduled visit for this patient."
              : "View all scheduled and completed visits for this patient."
            }
          </div>
        </div>
      )
    }

    if (activeSection === "History") {
      // Prescription history section - view medication adjustment history
      return (
        <div className="space-y-4">
          <PrescriptionHistory
            patientId={patient.profile_id}
            className="border-0 shadow-none"
          />
        </div>
      )
    }

    if (activeSection === "Tracking") {
      // Patient tracking section - view health metrics and medication tracking data
      return (
        <div className="space-y-4">
          <TrackingChart
            title="Patient Health Tracking"
            description="View patient's health metrics and medication tracking data"
            className="border-0 shadow-none bg-transparent"
            selectedMetric={currentMetricType}
            affirmation="Monitoring patient progress helps optimize treatment outcomes."
            medications={[]} // No medication overlay needed for admin view
            selectedMedications={selectedMedicationsForTracking}
            onMedicationSelectionChange={handleMedicationSelectionChange}
            onAddMetrics={() => {}} // Disabled for admins
            hasMetrics={healthMetricsData.length > 0}
            metricsData={healthMetricsData}
            patientId={patient.profile_id}
            onRefresh={handleRefreshMetrics}
            onMetricChange={handleMetricChange}
            medicationTrackingEntries={medicationTrackingData}
            showAddMetricsButton={false} // Hide button for admins
          />
        </div>
      )
    }

    // Patient Information section (default)
    return (
      <div className="space-y-4">
        <div className="bg-muted/50 rounded-xl p-4">
          <h3 className="font-semibold mb-2">Basic Information</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Name:</span>
              <p>{formatName(patient.first_name, patient.last_name)}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Date of Birth:</span>
              <p>{formatDate(patient.date_of_birth)}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Email:</span>
              <p>{patient.email}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Phone:</span>
              <p>{patient.phone || "Not provided"}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Patient ID:</span>
              <p>{patient.id.slice(0, 8)}...</p>
            </div>
            <div>
              <span className="text-muted-foreground">Intake Status:</span>
              <p>{getIntakeStatus(patient.has_completed_intake)}</p>
            </div>
          </div>
        </div>

        <div className="bg-muted/50 rounded-xl p-4">
          <h3 className="font-semibold mb-2">Treatment Information</h3>
          <div className="grid grid-cols-1 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Treatment Plan:</span>
              <p className="capitalize">{getTreatmentPlan()}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Primary Provider:</span>
              <p>{getPrimaryProvider()}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Member Since:</span>
              <p>{formatDate(patient.created_at)}</p>
            </div>
          </div>
        </div>

        {isAdmin && (
          <div className="bg-muted/50 rounded-xl p-4">
            <h3 className="font-semibold mb-2">Admin Actions</h3>
            <div className="flex gap-2 flex-wrap">
              <Badge variant="outline">Admin View</Badge>
              <Badge variant="secondary">Full Access</Badge>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "overflow-hidden p-0 bg-card",
          isFullscreen
            ? "!fixed !inset-0 !w-screen !h-screen !max-w-none !max-h-none !rounded-none !border-0 !left-0 !top-0 !transform-none"
            : "max-h-[80vh] md:max-h-[600px] w-[calc(100vw-32px)] max-w-[calc(100vw-32px)] sm:!w-[90vw] sm:!max-w-[900px] lg:!max-w-[1000px]"
        )}
      >
        <DialogTitle className="sr-only">
          Patient Information - {isAdmin ? 'Admin' : 'Provider'} View
        </DialogTitle>
        <DialogDescription className="sr-only">
          View and manage patient information from {isAdmin ? 'admin' : 'provider'} perspective.
        </DialogDescription>
        <SidebarProvider className="items-start flex-1 flex">
          <Sidebar 
            collapsible="none" 
            className="hidden md:flex w-64 border-r border-sidebar-border dark:border-transparent"
          >
            <SidebarContent>
              <SidebarGroup>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {patientNavData.nav.map((item) => (
                      <SidebarMenuItem key={item.name}>
                        <SidebarMenuButton
                          asChild
                          isActive={item.name === activeSection}
                        >
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault()
                              setActiveSection(item.name)
                              setSelectedPreference(null)
                              setSelectedOrder(null)
                            }}
                          >
                            <item.icon />
                            <span>{item.name}</span>
                          </a>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </SidebarContent>
          </Sidebar>
          <main 
            className="flex flex-1 flex-col overflow-hidden bg-card h-full min-h-0"
          >
            <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
              <div className="flex items-center gap-2 px-4 w-full">
                {/* Mobile Navigation Dropdown - only show on small screens */}
                <div className="flex md:hidden mr-2">
                  <Popover open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        className="w-auto justify-between border border-border hover:bg-muted px-3 h-8"
                      >
                        <span className="truncate">{activeSection}</span>
                        <ChevronDown className="h-4 w-4 ml-1 shrink-0" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 p-2" align="start">
                      <div className="space-y-1">
                        {patientNavData.nav.map((item) => (
                          <Button
                            key={item.name}
                            variant={item.name === activeSection ? "secondary" : "ghost"}
                            size="sm"
                            className="w-full justify-start"
                            onClick={() => {
                              setActiveSection(item.name)
                              setSelectedPreference(null)
                              setSelectedOrder(null)
                              setIsMobileMenuOpen(false)
                            }}
                          >
                            <item.icon className="mr-2 h-4 w-4" />
                            {item.name}
                          </Button>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 hidden md:block"
                  onClick={() => setIsFullscreen(!isFullscreen)}
                >
                  {isFullscreen ? (
                    <Minimize className="h-4 w-4" />
                  ) : (
                    <Maximize className="h-4 w-4" />
                  )}
                </Button>
                {getCurrentBreadcrumb()}
              </div>
            </header>
            <div className="flex flex-col gap-4 p-4 pt-0 flex-1 overflow-hidden">
              {renderContent()}
            </div>
          </main>
        </SidebarProvider>
      </DialogContent>
      
      {/* Reschedule Dialog */}
      {selectedAppointment && (
        <VisitsBookingDialog
          open={rescheduleDialogOpen}
          onOpenChange={setRescheduleDialogOpen}
          patientProfileId={patient?.profile_id || ''}
          medicationPreferences={medicationPreferences}
          providers={providers}
          onBookAppointment={async () => ({ success: false })} // Not used in reschedule mode
          onGetAvailableSlots={onGetAvailableSlots || (async () => ({ data: [] }))}
          isRescheduleMode={true}
          existingAppointment={{
            id: selectedAppointment.id,
            providerId: selectedAppointment.provider_id,
            providerName: selectedAppointment.provider_name,
            appointmentDate: selectedAppointment.appointment_date,
            startTime: selectedAppointment.start_time,
            treatmentType: selectedAppointment.treatment_type,
            appointmentType: selectedAppointment.appointment_type
          }}
          onRescheduleAppointment={onRescheduleAppointment}
        />
      )}
    </Dialog>
  )
}